/**
 * The browser's half of the support protocol (PLAN-NEXT H2).
 *
 * Five fetches and one hook, written the way `share/client.ts` and
 * `admin/client.ts` are written and for the same reason: the surface is a handful
 * of same-origin JSON calls carrying the session cookie the browser sends for
 * free, and a query cache would be more machinery than the whole feature.
 *
 * Every call answers an {@link AuthResult} — a value, or a sentence that can be
 * rendered — because this is the one screen in the product where a failure has to
 * be *explained*: somebody who cannot reach support has no second way to tell us
 * so. That is also why {@link SUPPORT_MESSAGES} exists: the Worker answers stable
 * codes rather than prose (`too_many_open_tickets`, `too_many_messages`,
 * `image_too_large`), and the prose belongs next to the form that provoked it.
 *
 * Opening a ticket and replying take an optional list of images. With none they
 * are the JSON posts they have always been; with any, the same call becomes one
 * `multipart/form-data` request with the same fields and the files beside them.
 * {@link checkSupportImages} applies the server's caps early, in the server's own
 * words, so a refusal costs a file picker rather than a round trip and a lost
 * paragraph — the server enforces every one of them again regardless.
 *
 * Every mutation resolves to the **whole thread**, which is what the server sends
 * back, so the screen after a reply is the server's state rather than an
 * optimistic guess spliced into a list. See `support/protocol.ts`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthResult } from '@/auth/client';
import type { SupportCategory, SupportThread, SupportTicketSummary } from './protocol';
import {
  SUPPORT_BASE_PATH,
  SUPPORT_IMAGE_CONTENT_TYPES,
  SUPPORT_IMAGE_MAX_BYTES,
  SUPPORT_MAX_IMAGES_PER_MESSAGE,
  supportClosePath,
  supportReplyPath,
  supportTicketPath,
} from './protocol';

const JSON_HEADERS = { accept: 'application/json' } as const;

/**
 * What each refusal means, in words a brewer can act on.
 *
 * Keyed by the Worker's stable codes. The two bounds are the interesting ones and
 * both say what to do instead of what went wrong — a cap you cannot see is only
 * frustrating if nobody tells you how to get under it.
 */
const SUPPORT_MESSAGES: Readonly<Record<string, string>> = {
  too_many_open_tickets:
    'You already have five conversations going. Reply on one of those, or close one you are finished with, and then open another.',
  too_many_messages:
    'This conversation has reached its length limit. Open a new one and mention this ticket in it.',
  bad_category: 'Choose what your question is about.',
  bad_request: 'Add a subject and a message before sending.',
  not_found: 'That conversation is not available any more.',
  unauthorized: 'Sign in to reach support.',
  too_many_images:
    'Three images per message is the most that fits. Send the rest in a follow-up reply.',
  image_too_large: 'That image is over 8 MB. Crop it or export it smaller and try again.',
  unsupported_image: 'Only PNG, JPEG and WebP images can be attached.',
  attachment_quota:
    'Your account has reached its 50 MB of attachments. The conversation still sends without the image.',
  attachments_not_configured:
    'Attachments are not available right now. Send the message without it and describe what the screenshot shows.',
};

const FALLBACK_MESSAGE = 'Support is not reachable right now. Try again in a moment.';

/** My threads, newest activity first. */
export function fetchSupportTickets(): Promise<AuthResult<readonly SupportTicketSummary[]>> {
  return read(SUPPORT_BASE_PATH, selectTickets);
}

/** One thread, with every message in it. */
export function fetchSupportThread(ticketId: string): Promise<AuthResult<SupportThread>> {
  return read(supportTicketPath(ticketId), selectThread);
}

/** What opening a ticket needs: what it is about, a line, and the message. */
export interface NewSupportTicket {
  readonly category: SupportCategory;
  readonly subject: string;
  readonly message: string;
}

/**
 * Open a ticket. Resolves to the thread it created, first message included.
 *
 * With no images this is the JSON call it has always been, byte for byte. With
 * images it becomes one `multipart/form-data` POST carrying the same three
 * fields and the files beside them — one request, because the screenshot is part
 * of the message rather than a thing with a life of its own (`protocol.ts`).
 */
export function createSupportTicket(
  input: NewSupportTicket,
  images: readonly File[] = [],
): Promise<AuthResult<SupportThread>> {
  if (images.length === 0) return post(SUPPORT_BASE_PATH, input, selectThread);
  const fields = { category: input.category, subject: input.subject, message: input.message };
  return postForm(SUPPORT_BASE_PATH, toForm(fields, images), selectThread);
}

/** Say something on a thread. Reopens it if it had been answered or closed. */
export function replySupportTicket(
  ticketId: string,
  message: string,
  images: readonly File[] = [],
): Promise<AuthResult<SupportThread>> {
  if (images.length === 0) return post(supportReplyPath(ticketId), { message }, selectThread);
  return postForm(supportReplyPath(ticketId), toForm({ message }, images), selectThread);
}

/**
 * Whether these files can be sent, and the sentence to show if not.
 *
 * The **server is the control** — it measures every body and sniffs every magic
 * number, and nothing here is trusted by it. What this buys is that a brewer who
 * picks a 40 MB video learns so from the file picker rather than from a round
 * trip that also loses the paragraph they had typed. It answers the *same*
 * sentences the server's codes map to, so being refused early and being refused
 * late read identically.
 *
 * The type check is the browser's `File.type`, which comes from the file's
 * extension and is exactly as trustworthy as that — which is fine here and would
 * not be on the server, where the bytes themselves are read instead.
 */
export function checkSupportImages(files: readonly File[]): string | null {
  if (files.length > SUPPORT_MAX_IMAGES_PER_MESSAGE) {
    return SUPPORT_MESSAGES.too_many_images ?? FALLBACK_MESSAGE;
  }
  for (const file of files) {
    if (file.size > SUPPORT_IMAGE_MAX_BYTES) {
      return SUPPORT_MESSAGES.image_too_large ?? FALLBACK_MESSAGE;
    }
    if (!(SUPPORT_IMAGE_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      return SUPPORT_MESSAGES.unsupported_image ?? FALLBACK_MESSAGE;
    }
  }
  return null;
}

/** Close a thread. Idempotent, and it notifies nobody. */
export function closeSupportTicket(ticketId: string): Promise<AuthResult<SupportThread>> {
  return post(supportClosePath(ticketId), {}, selectThread);
}

/** What {@link useSupportTickets} hands the section. */
export interface SupportTicketsState {
  readonly tickets: readonly SupportTicketSummary[];
  /** True until the first response lands. */
  readonly loading: boolean;
  /** Why the last read failed, or `null`. */
  readonly error: string | null;
  readonly refresh: () => void;
}

/**
 * The signed-in user's threads.
 *
 * `enabled` is what keeps a signed-out settings page from fetching at all — the
 * section renders nothing for a guest, and a hook that fetched anyway would put a
 * pointless 401 in everybody's network tab on every visit.
 *
 * A request counter rather than a `cancelled` flag, for the reason
 * `use-admin-data.ts` gives: the question is not "was this unmounted" but "is this
 * still the answer to the question being asked".
 */
export function useSupportTickets(enabled = true): SupportTicketsState {
  const [tickets, setTickets] = useState<readonly SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  const refresh = useCallback(() => {
    request.current += 1;
    const mine = request.current;
    setLoading(true);
    void fetchSupportTickets().then((result) => {
      if (request.current !== mine) return;
      setLoading(false);
      if (result.ok) {
        setTickets(result.value);
        setError(null);
        return;
      }
      setError(result.message);
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    return () => {
      request.current += 1;
    };
  }, [enabled, refresh]);

  return { tickets, loading, error, refresh };
}

// ---------------------------------------------------------------------------
// Plumbing
// ---------------------------------------------------------------------------

type JsonBody = Record<string, unknown>;

/**
 * Pull the payload out of a 200, or `null` if it is not the shape this build
 * reads.
 *
 * `null` becomes an ordinary failure with the generic message, exactly as
 * `share.ts` turns a document it cannot parse into "not found": a response the
 * client cannot read is a *failure*, and the one thing it must never be is a
 * crash on the settings page. This is also what makes the section safe to render
 * in a test whose `fetch` stub answers `{ ok: true }` and nothing else.
 */
function selectTickets(body: JsonBody): readonly SupportTicketSummary[] | null {
  return Array.isArray(body.tickets) ? (body.tickets as readonly SupportTicketSummary[]) : null;
}

function selectThread(body: JsonBody): SupportThread | null {
  const ticket = body.ticket;
  if (typeof ticket !== 'object' || ticket === null) return null;
  return Array.isArray((ticket as SupportThread).messages) ? (ticket as SupportThread) : null;
}

async function read<T>(path: string, select: (body: JsonBody) => T | null): Promise<AuthResult<T>> {
  try {
    const response = await fetch(path, { headers: JSON_HEADERS });
    if (!response.ok) return { ok: false, message: await readError(response) };
    const value = select((await response.json()) as JsonBody);
    return value === null ? { ok: false, message: FALLBACK_MESSAGE } : { ok: true, value };
  } catch {
    return { ok: false, message: FALLBACK_MESSAGE };
  }
}

async function post<T>(
  path: string,
  body: unknown,
  select: (body: JsonBody) => T | null,
): Promise<AuthResult<T>> {
  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: { ...JSON_HEADERS, 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) return { ok: false, message: await readError(response) };
    const value = select((await response.json()) as JsonBody);
    return value === null ? { ok: false, message: FALLBACK_MESSAGE } : { ok: true, value };
  } catch {
    return { ok: false, message: FALLBACK_MESSAGE };
  }
}

/**
 * The text fields and the files, as one multipart body.
 *
 * Every value is appended as a string, so the Worker reads the same three names
 * out of a form that it reads out of a JSON object. The files all go under one
 * name — `image` — because "up to three" is a repeated part rather than
 * `image1`, `image2`, `image3`, and the Worker asks for them with `getAll`.
 */
function toForm(fields: Record<string, string>, images: readonly File[]): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  for (const image of images) form.append('image', image);
  return form;
}

/**
 * A multipart POST.
 *
 * The same shape as {@link post} minus one line, and that line is the point:
 * **no `content-type` header**. A multipart body needs a boundary parameter in
 * that header, the boundary is generated by whatever wrote the body, and setting
 * the header by hand means declaring a boundary the `FormData` does not use —
 * which the server then cannot parse. Leaving it off lets the browser write both.
 */
async function postForm<T>(
  path: string,
  form: FormData,
  select: (body: JsonBody) => T | null,
): Promise<AuthResult<T>> {
  try {
    const response = await fetch(path, { method: 'POST', headers: JSON_HEADERS, body: form });
    if (!response.ok) return { ok: false, message: await readError(response) };
    const value = select((await response.json()) as JsonBody);
    return value === null ? { ok: false, message: FALLBACK_MESSAGE } : { ok: true, value };
  } catch {
    return { ok: false, message: FALLBACK_MESSAGE };
  }
}

/** The Worker's code, turned into the sentence for it. Never the code itself. */
async function readError(response: Response): Promise<string> {
  if (response.status === 401) return SUPPORT_MESSAGES.unauthorized ?? FALLBACK_MESSAGE;
  try {
    const body = (await response.json()) as { error?: string };
    return SUPPORT_MESSAGES[body.error ?? ''] ?? FALLBACK_MESSAGE;
  } catch {
    return FALLBACK_MESSAGE;
  }
}
